import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import API from '../../api-graphql';
import { config as constants } from '../../constants';
import { useStyles } from './styles';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useTour } from '@reactour/tour';
import { tourStepsDashboard } from '../TourSteps';

import {
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  Typography,
  Link,
  ListItemText,
  LinearProgress
} from '@material-ui/core';

import { CONSTANTS } from '../../constants/constants';
import { getUrlWithToken } from '../../utils';

const Dashboard = ({ config }) => {
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const { visibleTabs, subgroup, flows: flowTemplates } = config;
  const classes = useStyles();
  const { t } = useTranslation();
  const [state, setState] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tour = JSON.parse(localStorage.getItem('tour-dashboard'));
    setSteps(tourStepsDashboard(config));
    setCurrentStep(0);
    setIsOpen(tour);
    const init = async () => {
      const result = [];

      for (let i = 0; i < visibleTabs.length; i++) {
        const item = visibleTabs[i];
        let object = {
          id: item.name,
          title: t(`dashboard:${item.label}`),
          notFound: t('dashboard:documentDoesntExist'),
          url: item.url
        };
        switch (item.name) {
          case 'templates':
            object = {
              ...object,
              items: await getAllTemplates(subgroup)
            };
            break;
          case 'pending':
            object = {
              ...object,
              items: await getAllPending(subgroup)
            };

            break;
          case 'file':
            object = {
              ...object,
              items: await getAllFiles(subgroup)
            };
            break;
          case 'bundles':
            object = {
              ...object,
              items: await getAllBundles(subgroup)
            };
            break;
          case 'signed':
            object = {
              ...object,
              items: await getAllFinished(subgroup)
            };
            break;
          case 'notification':
            object.items = await getAllNotifications(subgroup);
            break;
          default:
            object.items = [];
            break;
        }
        if (item.name !== 'dashboard' && item.name !== 'identity') result.push(object);
        if (item.name === 'pending') {
          // @TO-DO Change to new admin api endpoint
          const card = {
            id: 'flowTemplates',
            title: t(`dashboard:flowTemplates`),
            notFound: t('dashboard:flowTemplatesDoesntExist'),
            items: flowTemplates
              .slice(0, 5)
              .map(e => ({ ...e, name: e.name }))
          };
          result.splice(1, 0, card);
        }
      }

      setState(result);
      setLoading(false);
    };

    if (visibleTabs) {
      init();
    }
  }, [visibleTabs]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Grid container justify='space-between' className={classes.root}>
      {state.map((card, i) => {
        return (
          <Grid item xs={12} md={6} key={i} className={classes.list}>
            <Typography variant='body1' style={{ letterSpacing: 0.15, textTransform: 'uppercase' }}>{card.title}</Typography>
            <Card className={classes.card}>
              <CardContent style={{ padding: 0 }}>
                <List disablePadding={false}>
                  {card.items.length > 0 ? (
                    card.items.map((item, key) => (
                      <div key={key}>
                        {item.flowId && (
                          <Link onClick={() => getUrlWithToken(`${constants.FIRMA_FRONT_URL}/validate/${item.flowId}`)}>
                            <ListItem button disableGutters>
                              <ListItemText
                                primary={item.name}
                                secondary={item.type === 'file' ? (
                                  t('dashboard:status') + t(`dashboard:${item.status}`) + ',   ' +
                                  t('dashboard:creationDate') +
                                  new Date(item.createdAt).toLocaleDateString()) : (
                                  t('dashboard:creationDate') +
                                  new Date(item.createdAt).toLocaleDateString()
                                )
                                }
                              />
                            </ListItem>
                          </Link>
                        )}
                        {!item.flowId && (
                          <Link
                            key={item.name}
                            component={RouterLink}
                            to={card.url
                              ? card.title === t('dashboard:LOTES') || card.title === t('dashboard:PENDIENTES')
                                ? `${card.url}/detail/${item.id}`
                                : `${card.url}/view/${item.id}`
                              : ''}
                            color='inherit'
                          >
                            <ListItem button disableGutters>
                              <ListItemText
                                primary={item.name}
                                secondary={item.type === 'file'
                                  ? (t('dashboard:status') + t(`dashboard:${item.status}`) + ',   ' +
                                    t('dashboard:creationDate') +
                                    new Date(item.createdAt).toLocaleDateString())
                                  : item.templateId
                                    ? t('dashboard:templateId') + item.templateId
                                    : (t('dashboard:creationDate') + new Date(item.createdAt).toLocaleDateString())
                                }
                              />
                            </ListItem>
                          </Link>
                        )}
                      </div>
                    ))
                  ) : (
                    <Typography variant='body1' className={classes.notFound}>
                      {card.notFound}
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const getAllPending = async (subgroup) => {
  const filter = {
    type: { eq: 'document' },
    status: {
      ne: 'deleted',
      in: [
        CONSTANTS.DOCUMENT.STATUS.INIT,
        CONSTANTS.DOCUMENT.STATUS.SEND_TO_SIGN,
        CONSTANTS.DOCUMENT.STATUS.REJECTED,
        CONSTANTS.DOCUMENT.STATUS.ERROR
      ]
    },
    subgroups: { contains: subgroup }
  };
  const [result] = await API.listDocuments(filter);
  return result.data.listDocuments.items;
};

const getAllFinished = async (subgroup) => {
  const filter = {
    type: { eq: 'document' },
    status: {
      ne: 'deleted',
      in: [
        CONSTANTS.DOCUMENT.STATUS.SIGNED,
        CONSTANTS.DOCUMENT.STATUS.CANCELLED
      ]
    },
    subgroups: { contains: subgroup }
  };
  const [result] = await API.listDocuments(filter);
  return result.data.listDocuments.items;
};

const getAllFiles = async (subgroup) => {
  const filter = {
    type: { eq: 'file' },
    status: { ne: 'deleted' },
    subgroups: { contains: subgroup }
  };
  const [result] = await API.listDocuments(filter);
  return result.data.listDocuments.items;
};

const getAllNotifications = async (subgroup) => {
  const filter = {
    type: { eq: 'notification' },
    status: { ne: 'deleted' },
    subgroups: { contains: subgroup }
  };
  const [result] = await API.listDocuments(filter);
  return result.data.listDocuments.items;
};

const getAllTemplates = async (subgroup) => {
  const filter = {
    status: { ne: 'deleted' },
    subgroups: { contains: subgroup }
  };
  const result = await API.listTemplates(filter);
  return result.data.listTemplates.items;
};

const getAllBundles = async (subgroup) => {
  const filter = {
    status: { ne: 'deleted' },
    subgroups: { contains: subgroup }
  };
  const [result] = await API.listBundles(filter);
  return result.data.listBundles.items;
};

const mapStateToProps = (state) => {
  return {
    config: state.config
  };
};

Dashboard.propTypes = {
  visibleTabs: PropTypes.array,
  subgroup: PropTypes.string,
  config: PropTypes.object.isRequired
};

export default connect(mapStateToProps, null)(Dashboard);
